import { useState } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { studentService, StudentWithStats, type StudentFilters } from '../../services';
import { LoadingSpinner, ConfirmDialog, FormField } from '../ui';
import { useToast, ToastProvider } from '../ui/Toast';
import {
  AddButton,
  CloseButton,
  EmptyState,
  Form,
  ManagementContainer,
  Modal,
  ModalActions,
  ModalButton,
  ModalContent,
  ModalHeader,
  ModalTitle,
  SearchInput,
  Section,
  SectionHeader,
  SectionTitle,
} from './shared/AdminPrimitives';

const FiltersContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SearchContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: center;
  flex-wrap: wrap;
`;

const FilterButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`;

const FilterButton = styled.button<{ $isActive?: boolean }>`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  ${({ $isActive, theme }) => 
    $isActive 
      ? `
        background-color: ${theme.colors.primary};
        color: ${theme.colors.white};
        border: 2px solid ${theme.colors.primary};
      `
      : `
        background-color: ${theme.colors.background};
        color: ${theme.colors.gray.dark};
        border: 1px solid ${theme.colors.gray.medium};
        
        &:hover {
          border-color: ${theme.colors.primary};
          background-color: ${theme.colors.primary}10;
        }
      `
  }
`;

const BulkActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.gray.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const BulkActionText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray.dark};
  margin-right: ${({ theme }) => theme.spacing.md};
`;

const BulkActionButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  border: none;

  ${({ $variant = 'secondary', theme }) =>
    $variant === 'primary'
      ? `
        background-color: ${theme.colors.primary};
        color: ${theme.colors.white};
        
        &:hover {
          background-color: ${theme.colors.secondary};
        }
      `
      : `
        background-color: ${theme.colors.gray.medium};
        color: ${theme.colors.white};
        
        &:hover {
          background-color: ${theme.colors.gray.dark};
        }
      `
  }
`;

const StudentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
`;

const StudentCard = styled.div<{ $isHidden?: boolean; $isSelected?: boolean }>`
  background: ${({ theme, $isHidden }) => 
    $isHidden ? theme.colors.gray.light : theme.colors.background};
  border: 2px solid ${({ theme, $isSelected, $isHidden }) => {
    if ($isSelected) return theme.colors.primary;
    if ($isHidden) return theme.colors.gray.medium;
    return theme.colors.gray.light;
  }};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  transition: all ${({ theme }) => theme.transitions.normal};
  opacity: ${({ $isHidden }) => $isHidden ? 0.7 : 1};
  position: relative;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
    transform: translateY(-2px);
  }
`;

const StudentCardCheckbox = styled.input`
  position: absolute;
  top: ${({ theme }) => theme.spacing.md};
  right: ${({ theme }) => theme.spacing.md};
  transform: scale(1.2);
  accent-color: ${({ theme }) => theme.colors.primary};
`;

const VisibilityBadge = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: ${({ theme }) => theme.spacing.md};
  left: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  
  ${({ $isVisible, theme }) => 
    $isVisible 
      ? `
        background-color: #10b981;
        color: ${theme.colors.white};
      `
      : `
        background-color: ${theme.colors.gray.medium};
        color: ${theme.colors.white};
      `
  }
`;

const StudentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const StudentAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.secondary} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: ${({ theme }) => theme.colors.white};
`;

const StudentInfo = styled.div`
  flex: 1;
`;

const StudentName = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
`;

const StudentMeta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray.dark};
`;

const StudentStats = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const StatLabel = styled.div`
  color: ${({ theme }) => theme.colors.gray.dark};
`;

const StudentActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
`;

const ToggleSlider = styled.span<{ $isOn: boolean }>`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme, $isOn }) => 
    $isOn ? '#10b981' : theme.colors.gray.medium};
  transition: 0.4s;
  border-radius: 24px;

  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: ${({ $isOn }) => $isOn ? '23px' : '3px'};
    bottom: 3px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'danger' }>`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.sm};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  ${({ $variant = 'primary', theme }) =>
    $variant === 'primary'
      ? `
        background-color: ${theme.colors.primary};
        color: ${theme.colors.white};
        
        &:hover {
          background-color: ${theme.colors.secondary};
        }
      `
      : `
        background-color: ${theme.colors.error};
        color: ${theme.colors.white};
        
        &:hover {
          background-color: ${theme.colors.error}dd;
        }
      `}
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const VisibilityHelp = styled.span`
  display: block;
  max-width: 19rem;
  margin-top: 0.25rem;
  color: #6b7280;
  font-size: 0.75rem;
  line-height: 1.35;
`;

const schema = yup.object({
  first_name: yup.string().required('First name is required'),
  last_name: yup.string().required('Last name is required'),
  grade_level: yup.string().optional(),
});

interface StudentFormData {
  first_name: string;
  last_name: string;
  grade_level?: string;
}

export function StudentManagement() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState<any>(null);
  const [deleteStudent, setDeleteStudent] = useState<any>(null);
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'visible' | 'hidden'>('all');
  const [selectedStudents, setSelectedStudents] = useState<Set<number>>(new Set());
  const toast = useToast();

  const invalidateVisibilityDependents = () => {
    const prefixes = [
      ['visible-students'],
      ['student'],
      ['student-artworks'],
      ['gallery-artworks'],
      ['tags'],
      ['exhibitions'],
      ['exhibition'],
      ['exhibition-artworks'],
      ['exhibition-artworks-preview'],
      ['homepage-data'],
      ['recent-artworks-strip'],
      ['artists-strip'],
      ['admin', 'artworks'],
      ['admin', 'recent-artworks'],
      ['admin', 'exhibitions'],
      ['admin', 'featured-exhibitions'],
      ['admin', 'tags'],
    ];
    prefixes.forEach((queryKey) => {
      queryClient.invalidateQueries({ queryKey, exact: false });
    });
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<StudentFormData>({
    resolver: yupResolver(schema),
  });

  // Fetch students with filters
  const filters: StudentFilters = {
    search: searchTerm || undefined,
    is_visible: visibilityFilter === 'all' ? undefined : visibilityFilter === 'visible'
  };
  
  const { data: studentsData, isLoading } = useQuery({
    queryKey: ['admin', 'students', filters],
    queryFn: () => studentService.getAllStudentsForAdmin(1, 100, filters),
  });

  // Create student mutation
  const createMutation = useMutation({
    mutationFn: (data: StudentFormData) => studentService.createStudent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'students'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['admin', 'student-stats'] });
      closeModal();
    },
  });

  // Update student mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: StudentFormData }) => 
      studentService.updateStudent(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'students'], exact: false });
      closeModal();
    },
  });

  // Delete student mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => studentService.deleteStudent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'students'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['admin', 'student-stats'] });
      setDeleteStudent(null);
    },
    onError: (error: any) => {
      console.error('Delete student error:', error);
      // Keep the dialog open so user can see the error
      // The error message will be displayed by the ConfirmDialog component
    },
  });

  // Toggle student visibility mutation
  const toggleVisibilityMutation = useMutation({
    mutationFn: ({ id, isVisible }: { id: number; isVisible: boolean }) => 
      studentService.setStudentVisibility(id, !isVisible),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'students'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['admin', 'student-stats'] });
      invalidateVisibilityDependents();
      const action = variables.isVisible ? 'hidden' : 'made visible';
      toast.showSuccess(`Student has been ${action}!`);
    },
    onError: (error: any) => {
      toast.showError(`Failed to update student visibility: ${error.message || 'Unknown error'}`);
    },
  });

  // Bulk visibility update mutation
  const bulkVisibilityMutation = useMutation({
    mutationFn: ({ studentIds, isVisible }: { studentIds: number[]; isVisible: boolean }) => 
      studentService.bulkUpdateVisibility(
        studentIds.map(id => ({ id, is_visible: isVisible }))
      ),
    onSuccess: (results, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'students'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['admin', 'student-stats'] });
      invalidateVisibilityDependents();
      const successCount = results.filter(r => r.success).length;
      const action = variables.isVisible ? 'made visible' : 'hidden';
      toast.showSuccess(`${successCount} student${successCount !== 1 ? 's' : ''} ${action}!`);
      setSelectedStudents(new Set());
    },
    onError: (error: any) => {
      toast.showError(`Failed to update student visibility: ${error.message || 'Unknown error'}`);
    },
  });

  const openAddModal = () => {
    setEditingStudent(null);
    reset({ first_name: '', last_name: '', grade_level: '' });
    setIsModalOpen(true);
  };

  const openEditModal = (student: StudentWithStats) => {
    const [firstName, ...rest] = student.name.split(/\s+/).filter(Boolean);
    setEditingStudent(student);
    setValue('first_name', firstName || '');
    setValue('last_name', rest.join(' '));
    setValue('grade_level', student.grade_level || '');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingStudent(null);
    reset();
  };

  const onSubmit = async (data: StudentFormData) => {
    if (editingStudent) {
      await updateMutation.mutateAsync({ id: editingStudent.student_id, data });
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const handleDelete = async () => {
    if (deleteStudent) {
      await deleteMutation.mutateAsync(deleteStudent.student_id);
    }
  };

  const students = studentsData?.students || [];
  
  const handleToggleVisibility = (student: StudentWithStats) => {
    toggleVisibilityMutation.mutate({
      id: student.student_id,
      isVisible: student.is_visible
    });
  };

  const handleStudentSelect = (studentId: number) => {
    setSelectedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedStudents.size === students.length) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(students.map(s => s.student_id)));
    }
  };

  const handleBulkVisibility = (isVisible: boolean) => {
    if (selectedStudents.size > 0) {
      bulkVisibilityMutation.mutate({
        studentIds: Array.from(selectedStudents),
        isVisible
      });
    }
  };

  const visibleCount = students.filter(s => s.is_visible).length;
  const hiddenCount = students.length - visibleCount;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <>
      <ToastProvider toasts={toast.toasts} onRemove={toast.removeToast} />
      <ManagementContainer>
        <Section>
          <SectionHeader>
            <SectionTitle>Student Management</SectionTitle>
            <AddButton onClick={openAddModal}>+ Add Student</AddButton>
          </SectionHeader>

          <FiltersContainer>
            <SearchContainer>
              <SearchInput
                type="text"
                placeholder="Search students by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ flex: 1, maxWidth: '400px' }}
              />
              <FilterButtons>
                <FilterButton
                  $isActive={visibilityFilter === 'all'}
                  onClick={() => setVisibilityFilter('all')}
                >
                  All ({students.length})
                </FilterButton>
                <FilterButton
                  $isActive={visibilityFilter === 'visible'}
                  onClick={() => setVisibilityFilter('visible')}
                >
                  ✓ Visible ({visibleCount})
                </FilterButton>
                <FilterButton
                  $isActive={visibilityFilter === 'hidden'}
                  onClick={() => setVisibilityFilter('hidden')}
                >
                  ⚫ Hidden ({hiddenCount})
                </FilterButton>
              </FilterButtons>
            </SearchContainer>

            {selectedStudents.size > 0 && (
              <BulkActions>
                <input
                  type="checkbox"
                  checked={selectedStudents.size === students.length}
                  onChange={handleSelectAll}
                  style={{ marginRight: '8px' }}
                />
                <BulkActionText>
                  {selectedStudents.size} student{selectedStudents.size !== 1 ? 's' : ''} selected
                </BulkActionText>
                <BulkActionButton
                  $variant="primary"
                  onClick={() => handleBulkVisibility(true)}
                  disabled={bulkVisibilityMutation.isPending}
                >
                  Make Visible
                </BulkActionButton>
                <BulkActionButton
                  onClick={() => handleBulkVisibility(false)}
                  disabled={bulkVisibilityMutation.isPending}
                >
                  Hide
                </BulkActionButton>
                <BulkActionButton
                  onClick={() => setSelectedStudents(new Set())}
                >
                  Clear
                </BulkActionButton>
              </BulkActions>
            )}
          </FiltersContainer>

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '64px 0' }}>
              <LoadingSpinner size="lg" text="Loading students..." />
            </div>
          ) : students.length === 0 ? (
            <EmptyState>
              <EmptyIcon>👥</EmptyIcon>
              <h3>No Students Found</h3>
              <p>
                {searchTerm || visibilityFilter !== 'all'
                  ? 'No students match your current filters.' 
                  : 'No students have been added yet. Add your first student to get started!'
                }
              </p>
            </EmptyState>
          ) : (
            <StudentsGrid>
              {students.map((student) => (
                <StudentCard 
                  key={student.student_id}
                  $isHidden={!student.is_visible}
                  $isSelected={selectedStudents.has(student.student_id)}
                >
                  <StudentCardCheckbox
                    type="checkbox"
                    checked={selectedStudents.has(student.student_id)}
                    onChange={() => handleStudentSelect(student.student_id)}
                  />
                  <VisibilityBadge $isVisible={student.is_visible}>
                    {student.is_visible ? '✓ Visible' : '⚫ Hidden'}
                  </VisibilityBadge>
                  
                  <StudentHeader>
                    <StudentAvatar>
                      {student.name.charAt(0).toUpperCase()}
                    </StudentAvatar>
                    <StudentInfo>
                      <StudentName>{student.name}</StudentName>
                      <StudentMeta>
                        Added {formatDate(student.created_at)} • {student.is_visible ? 'Visible in gallery' : 'Hidden from gallery'}
                      </StudentMeta>
                    </StudentInfo>
                  </StudentHeader>

                  <StudentStats>
                    <StatItem>
                      <StatValue>{student.artwork_count || 0}</StatValue>
                      <StatLabel>Artworks</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>{student.artwork_count}</StatValue>
                      <StatLabel>This Month</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>
                        {student.latest_artwork 
                          ? formatDate(student.latest_artwork)
                          : 'Never'
                        }
                      </StatValue>
                      <StatLabel>Last Upload</StatLabel>
                    </StatItem>
                  </StudentStats>

                  <StudentActions>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', color: '#6b7280' }}>Visibility:</span>
                      <ToggleSwitch>
                        <ToggleInput
                          type="checkbox"
                          checked={student.is_visible}
                          onChange={() => handleToggleVisibility(student)}
                          disabled={toggleVisibilityMutation.isPending}
                          aria-label={`${student.is_visible ? 'Hide' : 'Show'} ${student.name}`}
                          aria-describedby={`visibility-help-${student.student_id}`}
                        />
                        <ToggleSlider $isOn={student.is_visible} />
                      </ToggleSwitch>
                      <VisibilityHelp id={`visibility-help-${student.student_id}`}>
                        Hiding this artist also hides linked artwork from public pages. Staff can still manage it.
                      </VisibilityHelp>
                    </div>
                    <ActionButton onClick={() => openEditModal(student)}>
                      Edit
                    </ActionButton>
                    <ActionButton
                      $variant="danger"
                      onClick={() => setDeleteStudent(student)}
                    >
                      Delete
                    </ActionButton>
                  </StudentActions>
                </StudentCard>
              ))}
            </StudentsGrid>
          )}
        </Section>
      </ManagementContainer>

      {/* Add/Edit Modal */}
      <Modal $isOpen={isModalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {editingStudent ? 'Edit Student' : 'Add New Student'}
            </ModalTitle>
            <CloseButton onClick={closeModal}>×</CloseButton>
          </ModalHeader>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <FormField
              name="first_name"
              label="First Name"
              placeholder="Enter first name"
              register={register}
              errors={errors}
              required
            />

            <FormField
              name="last_name"
              label="Last Name"
              placeholder="Enter last name"
              register={register}
              errors={errors}
              required
            />

            <FormField
              name="grade_level"
              label="Grade Level"
              placeholder="Enter grade level (optional)"
              register={register}
              errors={errors}
            />

            <ModalActions>
              <ModalButton type="button" $variant="secondary" onClick={closeModal}>
                Cancel
              </ModalButton>
              <ModalButton
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending 
                  ? 'Saving...' 
                  : editingStudent ? 'Update Student' : 'Add Student'
                }
              </ModalButton>
            </ModalActions>
          </Form>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation */}
      {deleteStudent && (
        <ConfirmDialog
          isOpen={!!deleteStudent}
          title="Delete Student"
          message={`Are you sure you want to delete "${deleteStudent.name}"? This action cannot be undone. Note: Students with uploaded artwork cannot be deleted - please remove their artwork first.`}
          details={deleteMutation.error ? (deleteMutation.error as any)?.message || 'An error occurred during deletion.' : undefined}
          confirmText="Delete Student"
          cancelText="Cancel"
          onConfirm={handleDelete}
          onCancel={() => setDeleteStudent(null)}
          variant="danger"
          loading={deleteMutation.isPending}
        />
      )}
    </>
  );
}
